// frontend/src/components/notifications/NotificationDeliveryLog.js
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, TrendingUp, MessageSquare, Calendar } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedCard, ThemedSelect, ThemedBadge } from '../themed';
import notificationService from '../../services/notificationService';
import { getNotificationChannelIcon } from '../../utils/notificationChannelMetadata';

/**
 * Notification delivery log and statistics component for admin users
 * Shows delivery statistics and performance metrics
 */
const NotificationDeliveryLog = ({ entityId, channelStatistics, manageableChannels = [] }) => {
    const { t } = useTranslation();
    const { background, text, border, cn } = useTheme();
    const permittedChannelSet = useMemo(() => new Set((manageableChannels || []).map(channel => channel.channel)), [manageableChannels]);

    console.log('NotificationDeliveryLog Debug:', {
        manageableChannels,
        permittedChannelSet: Array.from(permittedChannelSet),
        channelStatistics,
        entityId
    });

    const filterStatistics = (stats = {}) => {
        if (permittedChannelSet.size === 0) {
            return {};
        }
        return Object.fromEntries(
            Object.entries(stats || {}).filter(([channelName]) => permittedChannelSet.has(channelName))
        );
    };
    const [loading, setLoading] = useState(true);
    const [timeframe, setTimeframe] = useState(30);
    const [statistics, setStatistics] = useState(filterStatistics(channelStatistics));

    useEffect(() => {
        setStatistics(filterStatistics(channelStatistics));
    }, [channelStatistics, permittedChannelSet]);

    useEffect(() => {
        loadStatistics();
    }, [entityId, timeframe, permittedChannelSet]);

    /**
     * Load delivery statistics
     */
    const loadStatistics = async () => {
        try {
            setLoading(true);
            if (permittedChannelSet.size === 0) {
                console.log('No permitted channels, skipping statistics load');
                setStatistics({});
                return;
            }

            const response = await notificationService.getChannelStatistics(entityId, timeframe);
            console.log('Statistics API Response:', response);
            // Extract the channelStats from the response
            const stats = response.channelStats || {};
            console.log('Extracted channelStats:', stats);
            const filtered = filterStatistics(stats);
            console.log('Filtered Statistics:', filtered);
            setStatistics(filtered);
        } catch (error) {
            console.error('Error loading statistics:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className={cn("flex justify-center items-center h-32", text.secondary)}>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-3">{t('loadingStatistics')}...</span>
            </div>
        );
    }

    const hasData = Object.keys(statistics).length > 0;
    const totalSent = Object.values(statistics).reduce((sum, stat) => sum + (parseInt(stat.totalSent) || 0), 0);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className={cn("text-lg font-semibold", text.primary)}>
                        {t('notificationDeliveryLog')}
                    </h2>
                    <p className={cn("text-sm", text.secondary)}>
                        {t('viewDeliveryStatisticsAndPerformance')}
                    </p>
                </div>
                
                <div className="flex items-center space-x-4">
                    <label className={cn("text-sm font-medium", text.primary)}>
                        {t('timeframe')}:
                    </label>
                    <ThemedSelect
                        value={timeframe}
                        placeholder={t('selectTimeframe')}
                        onChange={(e) => setTimeframe(parseInt(e.target.value))}
                        className="w-32"
                    >
                        <option value={1}>{t('today')}</option>
                        <option value={7}>{t('lastWeek')}</option>
                        <option value={30}>{t('lastMonth')}</option>
                        <option value={90}>{t('last3Months')}</option>
                    </ThemedSelect>
                </div>
            </div>

            {!hasData ? (
                <ThemedCard className="p-8">
                    <div className={cn("text-center", text.secondary)}>
                        <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <h3 className={cn("text-lg font-medium mb-2", text.primary)}>
                            {t('noDeliveryData')}
                        </h3>
                        <p>
                            {t('noNotificationsDeliveredInTimeframe', { days: timeframe })}
                        </p>
                    </div>
                </ThemedCard>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <ThemedCard className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className={cn("text-sm font-medium", text.secondary)}>
                                        {t('totalSent')}
                                    </p>
                                    <p className={cn("text-2xl font-bold", text.primary)}>
                                        {totalSent.toLocaleString()}
                                    </p>
                                </div>
                                <TrendingUp className={cn("w-8 h-8", "text-blue-500")} />
                            </div>
                        </ThemedCard>

                        <ThemedCard className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className={cn("text-sm font-medium", text.secondary)}>
                                        {t('overallSuccessRate')}
                                    </p>
                                    <p className={cn("text-2xl font-bold", text.primary)}>
                                        {totalSent > 0 ? (
                                            Object.values(statistics).reduce((sum, stat) => sum + (parseInt(stat.successful) || 0), 0) / totalSent * 100
                                        ).toFixed(1) : 0}%
                                    </p>
                                </div>
                                <BarChart3 className={cn("w-8 h-8", "text-green-500")} />
                            </div>
                        </ThemedCard>

                        <ThemedCard className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className={cn("text-sm font-medium", text.secondary)}>
                                        {t('activeChannels')}
                                    </p>
                                    <p className={cn("text-2xl font-bold", text.primary)}>
                                        {Object.keys(statistics).length}
                                    </p>
                                </div>
                                <Calendar className={cn("w-8 h-8", "text-purple-500")} />
                            </div>
                        </ThemedCard>

                        <ThemedCard className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className={cn("text-sm font-medium", text.secondary)}>
                                        {t('failedDeliveries')}
                                    </p>
                                    <p className={cn("text-2xl font-bold", text.primary)}>
                                        {Object.values(statistics).reduce((sum, stat) => sum + (parseInt(stat.failed) || 0), 0)}
                                    </p>
                                </div>
                                <MessageSquare className={cn("w-8 h-8", "text-red-500")} />
                            </div>
                        </ThemedCard>
                    </div>

                    {/* Channel Statistics */}
                    <ThemedCard className="p-6">
                        <h3 className={cn("text-lg font-medium mb-4", text.primary)}>
                            {t('channelPerformance')}
                        </h3>
                        
                        <div className="space-y-4">
                            {Object.entries(statistics).map(([channel, stats]) => {
                                const Icon = getNotificationChannelIcon(channel, BarChart3);
                                const successRate = stats.totalSent > 0 ? (stats.successful / stats.totalSent * 100) : 0;
                                
                                return (
                                    <div key={channel} className={cn("p-4 rounded-lg border", background.secondary, border.primary)}>
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center space-x-3">
                                                <Icon className="w-5 h-5" />
                                                <span className={cn("font-medium", text.primary)}>
                                                    {t(`${channel}Notifications`)}
                                                </span>
                                                <ThemedBadge 
                                                    variant={successRate >= 95 ? "success" : successRate >= 80 ? "warning" : "error"}
                                                >
                                                    {successRate.toFixed(1)}% {t('success')}
                                                </ThemedBadge>
                                            </div>
                                        </div>
                                        
                                        <div className="grid grid-cols-4 gap-4 text-sm">
                                            <div>
                                                <p className={cn("font-medium", text.secondary)}>{t('sent')}</p>
                                                <p className={cn("text-lg", text.primary)}>{stats.totalSent || 0}</p>
                                            </div>
                                            <div>
                                                <p className={cn("font-medium", text.secondary)}>{t('successful')}</p>
                                                <p className={cn("text-lg", "text-green-600")}>{stats.successful || 0}</p>
                                            </div>
                                            <div>
                                                <p className={cn("font-medium", text.secondary)}>{t('delivered')}</p>
                                                <p className={cn("text-lg", "text-blue-600")}>{stats.delivered || 0}</p>
                                            </div>
                                            <div>
                                                <p className={cn("font-medium", text.secondary)}>{t('failed')}</p>
                                                <p className={cn("text-lg", "text-red-600")}>{stats.failed || 0}</p>
                                            </div>
                                        </div>

                                        {/* Progress bar */}
                                        <div className="mt-3">
                                            <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                                                <div 
                                                    className="bg-green-600 h-2 rounded-full transition-all" 
                                                    style={{ width: `${successRate}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </ThemedCard>

                    {/* Performance Insights */}
                    <ThemedCard className="p-6">
                        <h3 className={cn("text-lg font-medium mb-4", text.primary)}>
                            {t('performanceInsights')}
                        </h3>
                        
                        <div className="space-y-3">
                            {Object.entries(statistics).map(([channel, stats]) => {
                                const successRate = stats.totalSent > 0 ? (stats.successful / stats.totalSent * 100) : 0;
                                let insight = '';
                                let color = '';
                                
                                if (successRate >= 95) {
                                    insight = t('excellentPerformance');
                                    color = 'text-green-600';
                                } else if (successRate >= 80) {
                                    insight = t('goodPerformanceWithRoomForImprovement');
                                    color = 'text-yellow-600';
                                } else {
                                    insight = t('performanceNeedsAttention');
                                    color = 'text-red-600';
                                }
                                
                                return (
                                    <div key={channel} className="flex items-center justify-between py-2">
                                        <span className={cn("font-medium", text.primary)}>
                                            {t(`${channel}Notifications`)}
                                        </span>
                                        <span className={cn("text-sm", color)}>
                                            {insight}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </ThemedCard>
                </>
            )}
        </div>
    );
};

export default NotificationDeliveryLog;
